import {List} from 'immutable';
import i18next from 'i18next';
import moment from 'moment-timezone';

import Draft from 'web-app/react/entities/drafts/model';
import ObservationBehaviors from 'web-app/behaviors/observation-behavior-ids';
import {ObservationVariant} from 'web-app/react/entities/observations/constants';
import * as AssessmentHelpers from 'web-app/react/feed/post/new-post/new-assessment/helpers';
import {getRecipientString} from 'web-app/util/string';
import * as SentryService from 'web-app/services/sentry';
import {isNonEmpty} from 'web-app/util/non-empty-array';
import {
    type ObservationStatusState,
    ObservationVariant as ObservationVariantType,
    Refinement,
} from 'web-app/util/graphql/api-types';
import type FileRecord from 'web-app/react/components/file-upload/file-record';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {type ObservationDataFragment} from './__generated__/fragments.api-types';

export const getRefinementText = (refinement: Refinement) => {
    switch (refinement) {
        case Refinement.EMERGING:
            return i18next.t('childDevelopment.refinements.EMERGING');
        case Refinement.DEVELOPING:
            return i18next.t('childDevelopment.refinements.DEVELOPING');
        case Refinement.SECURE:
            return i18next.t('childDevelopment.refinements.SECURE');
        default:
            return '';
    }
};

export const getVariantDisplayText = (variant: ObservationVariantType) => {
    switch (variant) {
        case ObservationVariant.REGULAR_OBSERVATION:
            return i18next.t('childDevelopment.variantDisplay.display_REGULAR_OBSERVATION');
        case ObservationVariant.PARENT_OBSERVATION:
            return i18next.t('childDevelopment.variantDisplay.display_PARENT_OBSERVATION');
        case ObservationVariant.ASSESSMENT:
            return i18next.t('childDevelopment.variantDisplay.display_ASSESSMENT');
        case ObservationVariant.TWO_YEAR_PROGRESS:
            return i18next.t('childDevelopment.variantDisplay.display_TWO_YEAR_PROGRESS');
        case ObservationVariant.UP_TO_SPEED_OBSERVATION:
            return i18next.t('childDevelopment.variantDisplay.display_UP_TO_SPEED_OBSERVATION');
        default:
            return '';
    }
};

export const getVariantAmbiguityText = (variant: ObservationVariantType, options?: {plural: boolean}) => {
    const {plural} = options || {};

    switch (variant) {
        case ObservationVariantType.REGULAR_OBSERVATION:
            return i18next.t('childDevelopment.variantAmbiguity.REGULAR_OBSERVATION', {count: plural ? 2 : 1});
        case ObservationVariantType.PARENT_OBSERVATION:
            return i18next.t('childDevelopment.variantAmbiguity.PARENT_OBSERVATION', {count: plural ? 2 : 1});
        case ObservationVariantType.ASSESSMENT:
            return i18next.t('childDevelopment.variantAmbiguity.ASSESSMENT', {count: plural ? 2 : 1});
        case ObservationVariantType.TWO_YEAR_PROGRESS:
            return i18next.t('childDevelopment.variantAmbiguity.TWO_YEAR_PROGRESS', {count: plural ? 2 : 1});
        case ObservationVariantType.UP_TO_SPEED_OBSERVATION:
            return i18next.t('childDevelopment.variantAmbiguity.UP_TO_SPEED_OBSERVATION', {count: plural ? 2 : 1});
        default:
            return '';
    }
};

export const getStatusText = (status: ObservationStatusState) => {
    switch (status) {
        case 'APPROVED':
            return i18next.t('childDevelopment.observations.APPROVED');
        case 'CHANGES_REQUESTED':
            return i18next.t('childDevelopment.observations.CHANGES_REQUESTED');
        case 'DRAFT':
            return i18next.t('childDevelopment.observations.DRAFT');
        case 'PENDING_REVIEW':
            return i18next.t('childDevelopment.observations.PENDING_REVIEW');
        case 'SENT':
            return i18next.t('childDevelopment.observations.SENT_TO_PARENTS');
        default:
            return '';
    }
};

export const getAmbiguousVariants = behaviors => {
    const behavior = behaviors.behaviorForId(ObservationBehaviors.ObservationVariantAmbiguity);

    if (!behavior) {
        return undefined;
    }

    return behavior.payload.variants.map(variant => ({
        label: getVariantAmbiguityText(variant),
        value: variant,
    }));
};

export const getObservationDeleteConfirmationByVariant = variant => {
    switch (variant) {
        case ObservationVariant.ASSESSMENT:
            return i18next.t('feed.confirmDeleteAssessment');
        default:
            return i18next.t('feed.confirmDeleteObservation');
    }
};

export const getObservationDeleteTitleByVariant = variant => {
    switch (variant) {
        case ObservationVariant.ASSESSMENT:
            return i18next.t('feed.deleteAssessmentSuccess');
        default:
            return i18next.t('feed.deleteObservationSuccess');
    }
};

export const getObservationUpdateTitleByVariant = variant => {
    switch (variant) {
        case ObservationVariant.ASSESSMENT:
            return i18next.t('feed.updateAssessmentSuccess');
        default:
            return i18next.t('feed.updateObservationSuccess');
    }
};

const getMiddleOfTitle = observation => {
    switch (observation.variant) {
        case ObservationVariant.TWO_YEAR_PROGRESS:
            return i18next.t('feed.madeTwoYearProgressCheck');
        case ObservationVariant.ASSESSMENT:
            return i18next.t('feed.assessedTheProgress');
        case ObservationVariant.REGULAR_OBSERVATION:
        case ObservationVariant.PARENT_OBSERVATION:
        default:
            return i18next.t('feed.madeAnObservation');
    }
};

const getCreatorNameFromObservation = observation => {
    // Observation is required in the api but sometimes we get a null value
    if (!observation) {
        SentryService.captureMessage('Observation is null');
    }
    // Created by is required in the api but sometimes we get a null value
    if (!observation.createdBy) {
        SentryService.captureMessage('Created by is null', {
            extra: {
                observationId: observation.id,
            },
        });
    }
    return observation.createdBy ? observation.createdBy.name.fullName : i18next.t('unknown');
};

export const storyTitleListForObservation = observation => {
    const creatorName = getCreatorNameFromObservation(observation);
    const middleOfTitle = getMiddleOfTitle(observation);

    const children = getRecipientString((observation.children ?? []).map(({name}: any) => name));

    return List([creatorName, middleOfTitle, children]);
};

const getFilteredArea = (area, obsStatements, obsAreas) => {
    // Base case
    if (area.statements && isNonEmpty(area.statements)) {
        const filteredStatements = area.statements
            .map(statement => {
                const obsStatement = obsStatements.find(obsStatement => statement.id === obsStatement.statement.id);
                if (!obsStatement) {
                    return null;
                }

                return {
                    ...statement,
                    refinement: obsStatement.refinement,
                };
            })
            .filter(a => a);

        const areaRefinement = obsAreas.find(a => a.area.id === area.id);

        if (filteredStatements.length > 0 || areaRefinement) {
            return {
                ...area,
                statements: filteredStatements,
                overallRefinement: areaRefinement && areaRefinement.refinement,
                note: areaRefinement && areaRefinement.note,
            };
        }
    }

    // Recursion through subAreas
    if (area.subAreas && isNonEmpty(area.subAreas)) {
        const filteredSubAreas = area.subAreas
            .map(subArea => getFilteredArea(subArea, obsStatements, obsAreas))
            .filter(a => a);

        const targetArea = obsAreas.find(a => a.area.id === area.id);

        if (filteredSubAreas.length > 0) {
            return {
                ...area,
                note: targetArea && targetArea.note,
                subAreas: filteredSubAreas,
            };
        }
    }

    /**
     * If the area has been directly targeted with a refinement
     * we return it as an area with no statements, no subAreas and
     * just the refinement. This allows the UI to render the area
     * with just the title and color.
     */
    const targetArea = obsAreas.find(a => a.area.id === area.id);
    if (targetArea) {
        return {
            ...area,
            statements: [],
            subAreas: [],
        };
    }

    return null;
};

const getMappedRefinements = (statements, areas = [], framework) => ({
    ...framework,
    areas: framework.areas.map(area => getFilteredArea(area, statements, areas)).filter(a => a),
});

export const getFilesFromObservation = observation => {
    return List<FileRecord>(observation.videos.map(video => Draft.videoToFileRecord(video)).filter(hasValue))
        .concat<FileRecord>(observation.images.map(Draft.imageToFileRecord))
        .concat<FileRecord>(observation.files.map(Draft.fileToFileRecord));
};

export const getMappedBehaviors = obs => {
    if (obs.behaviors) {
        return obs.behaviors.map(({id, ...rest}) => {
            if (rest) {
                return {
                    id,
                    payload: {
                        ...rest,
                    },
                };
            } else {
                return {
                    id,
                };
            }
        });
    }
    return [];
};

export const observationToFormData = (observation, withFiles = false) => ({
    files: withFiles ? getFilesFromObservation(observation) : undefined,
    body: observation.remark && observation.remark.body,
    nextStep: observation.nextStep ? observation.nextStep.body : '',
    selectedRecipients: observation.children.map(({id}) => id).join(','),
    observedAt:
        observation.remark && observation.remark.date
            ? observation.remark.date
            : moment(observation.status.createdAt).format('YYYY-MM-DD'),
    statements: observation.remark.statements
        ? observation.remark.statements.map(s => ({
              statementId: s.statement.id,
              refinement: s.refinement,
          }))
        : [],
    areaRefinements: AssessmentHelpers.prepareAreaRefinementForForm(observation.variant, observation.remark.areas),
});

export const getMappedStatementsByFramework = (statements, areas, frameworks) =>
    frameworks.map(framework => getMappedRefinements(statements, areas, framework)).filter(_ => _);

export const convertToStoryVideos = (videos: ObservationDataFragment['videos']) =>
    List(videos).map(video => ({
        ...video,
        transcoding: video.__typename === 'TranscodingVideo',
        videoId: video.id,
    }));

export const convertToStoryImages = images =>
    List(images).map((i: any) => ({
        ...i,
        imageId: i.id,
        prefix: `${i.secret.prefix}/${i.secret.key}`,
        key: `${i.secret.path}?expires=${i.secret.expires}`,
    }));

export const convertToStoryFiles = files =>
    List(files).map((f: any) => ({
        fileId: f.id,
        filename: f.name,
        url: f.url,
    }));

export const collectFrameworkIdsFromObservations = observations => {
    const observationList = List(observations);
    return observationList
        .flatMap((obs: any) => obs.remark.statements)
        .map(({statement}: any) => statement.area.frameworkId)
        .concat(observationList.flatMap((obs: any) => obs.remark.areas).map(({area}: any) => area.frameworkId))
        .filter(a => a)
        .toSet()
        .toArray();
};
